'use client';

import { useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { ActionButtons } from '@/components/molecules';
import { ModalWrapper } from '@/components/organisms';
import { getMatchupStatusApiValue, MatchCompletedEnum, MatchStatusApiMap, MatchStatusEnum, MatchupStatusEnum } from '@/domain/constants/constants';
import { SubMatch } from '@/entities/matches/Matches';
import { useUpdateMatchMutation } from '@/hooks/matches/useUpdateMatch';
import { useUpdateMatchup } from '@/hooks/matches/useUpdateMatchup';

interface Props {
	isOpen: boolean;
	onClose: () => void;
	matchupId: string;
	incompleteMatches: SubMatch[];
}

export const CompleteMatchesModal = ({ isOpen, onClose, matchupId, incompleteMatches }: Props) => {
	const modalDataRef = useRef<SubMatch[]>([]);
	const [isInitialized, setIsInitialized] = useState(false);

	const updateMatchupMutation = useUpdateMatchup();
	const updateMatchMutation = useUpdateMatchMutation({
		matchupId: matchupId
	});

	// Initialize snapshot when modal opens
	if (isOpen && !isInitialized) {
		modalDataRef.current = incompleteMatches;
		setIsInitialized(true);
	}

	// Reset when modal closes
	if (!isOpen && isInitialized) {
		setIsInitialized(false);
	}

	const handleCompleteMatches = async () => {
		const snapshot = modalDataRef.current;

		try {
			// Update all incomplete matches in parallel and wait for all to complete
			await Promise.all(
				snapshot.map((match) => {
					return updateMatchMutation.mutateAsync({
						matchId: match.matchUuid,
						updateData: {
							matchStatus: MatchStatusApiMap[MatchStatusEnum.COMPLETED_MATCH_STATUS],
							matchCompletedType: MatchCompletedEnum.CANCELED_MATCH_COMPLETED_TYPE
						}
					});
				})
			);

			// After all matches are successfully updated, update the matchup status
			await updateMatchupMutation.mutateAsync({
				matchup_uuid: matchupId,
				matchup_status: getMatchupStatusApiValue(MatchupStatusEnum.COMPLETED_MATCHUP_STATUS)
			});

			toast.success('All matches completed successfully!');
			onClose();
		} catch (_error) {
			toast.error('Failed to complete matches. Please try again.');
			onClose();
		}
	};

	if (!isOpen) return null;

	const isPending = updateMatchMutation.isPending || updateMatchupMutation.isPending;
	const displayMatches = modalDataRef.current;

	return (
		<ModalWrapper title="Complete Matches" onClose={onClose} className="max-w-[600px]" closeOnOutsideClick={!isPending}>
			<hr className="text-gray-400 my-4" />
			<div className="flex flex-col gap-4 px-2">
				<div className="flex flex-col gap-3">
					<p className="text-base font-medium text-gray-900">
						Are you sure you want to complete all matches? The following {displayMatches.length} match
						{displayMatches.length > 1 ? 'es' : ''} ha{displayMatches.length > 1 ? 've' : 's'} not been played:
					</p>

					<div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
						{displayMatches.map((match) => (
							<div key={match.matchUuid} className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0">
								<div className="flex flex-col">
									<span className="font-medium text-gray-900">
										{match.playerGroupTitle} {match.formatTitle !== 'No title for this match' ? `- ${match.formatTitle}` : ''}
									</span>
									{match.roundText ? <span className="text-sm text-gray-600">{match.roundText}</span> : null}
								</div>
								<span className="text-sm text-orange-600 font-medium">Not played</span>
							</div>
						))}
					</div>

					<p className="text-sm text-gray-600">All incomplete matches will be marked as canceled and the matchup will be completed.</p>
				</div>

				<ActionButtons
					className="flex justify-end gap-3 pt-4"
					closeButtonClassName="w-32 text-gray-700"
					submitButtonClassName="w-32 bg-orange-600 text-white hover:bg-orange-700"
					onClose={() => {
						if (!isPending) {
							onClose();
						}
					}}
					onSubmit={handleCompleteMatches}
					submitButtonText={isPending ? 'Completing...' : 'Complete'}
					closeButtonText="Cancel"
					submitButtonDisabled={isPending}
				/>
			</div>
		</ModalWrapper>
	);
};
